import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import SyncIcon from '@mui/icons-material/Sync';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SendIcon from '@mui/icons-material/Send';
import UndoIcon from '@mui/icons-material/Undo';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import apiService from '../services/api.service';
import {
  KSA_CATEGORIES,
  KSA_CATEGORY_LABELS,
  ReportData,
  ReportIssue,
  ReportView,
  TEMPLATE_LABELS,
} from '../types/projectReport';
import { contractTotal, dateLabel, durationDays, formatAmount, pendingNcrs, setPath } from '../utils/projectReport';
import { ComputedValue, DateInput, NumberInput, TextInput } from '../components/projectReport/ReportInputs';
import IssueList, { issueKey } from '../components/projectReport/IssueList';
import SubcontractorsTable from '../components/projectReport/SubcontractorsTable';

/**
 * One project's monthly progress report, in the order of the company's template (KSA: Engie
 * "Executive Summary"; KWT: Dhow Engineering). SAP / MCSR fill what they know; the project manager
 * enters the rest, ranks every safety issue and concern, and submits.
 */

const errorText = (e: any, fallback: string): string => e?.response?.data?.message || e?.message || fallback;

const Section: React.FC<{ no: number; title: string; children: React.ReactNode; aside?: React.ReactNode }> = ({ no, title, children, aside }) => (
  <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 } }} component="section" aria-labelledby={`sec-${no}`}>
    <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1.5 }}>
      <Typography id={`sec-${no}`} variant="h6" component="h2" sx={{ fontSize: '1.05rem' }}>{no}. {title}</Typography>
      {aside}
    </Stack>
    {children}
  </Paper>
);

const ProjectReportEditor: React.FC = () => {
  const { id } = useParams();
  const reportId = Number(id);
  const navigate = useNavigate();
  const [view, setView] = useState<ReportView | null>(null);
  const [data, setData] = useState<ReportData | null>(null);
  const [issues, setIssues] = useState<ReportIssue[]>([]);
  const [reportNo, setReportNo] = useState<number | null>(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ severity: 'success' | 'info' | 'warning' | 'error'; text: string } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [invalid, setInvalid] = useState<Set<string>>(new Set());
  const [dialog, setDialog] = useState<null | 'submit' | 'return' | 'delete'>(null);
  const [returnNote, setReturnNote] = useState('');

  const apply = (v: ReportView) => {
    setView(v);
    setData(v.data);
    setIssues(v.issues.map((i) => ({ ...i, key: `i${i.issueId}` })));
    setReportNo(v.reportNo);
    setDirty(false);
    setInvalid(new Set());
  };

  const load = useCallback(async () => {
    try {
      const res: any = await apiService.getProjectReport(reportId);
      apply(res.data);
    } catch (e: any) {
      setLoadError(errorText(e, 'The report could not be loaded'));
    }
  }, [reportId]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  if (loadError) return <Box sx={{ p: 3 }}><Alert severity="warning">{loadError}</Alert></Box>;
  if (!view || !data) {
    return <Stack direction="row" spacing={1.5} sx={{ p: 3 }} role="status"><CircularProgress size={20} /><Typography>Loading the report…</Typography></Stack>;
  }

  const t = view.template;
  const editable = view.permissions.canEdit && !busy;
  const set = (path: string, value: unknown) => {
    setData((d) => (d ? setPath(d, path, value) : d));
    setDirty(true);
  };
  /** The SAP hint props of a field: SAP's value and "use SAP value". */
  const sap = (path: string) => {
    const v = view.sapValues?.[path];
    return v === undefined || v === null ? {} : { sap: v, onUseSap: () => set(path, v) };
  };
  const setIssueList = (next: ReportIssue[]) => { setIssues(next); setDirty(true); };

  const validate = (): string | null => {
    const bad = new Set<string>();
    for (const i of issues) {
      if (!i.description.trim() && !i.action.trim()) continue;
      if (!i.rank || !i.description.trim()) bad.add(issueKey(i));
    }
    setInvalid(bad);
    return bad.size ? `Give every issue a rank and a description (${bad.size} to complete).` : null;
  };
  const payload = () => ({
    data,
    issues: issues
      .filter((i) => i.description.trim() || i.action.trim())
      .map(({ issueId, key, ...rest }) => rest),
    reportNo: reportNo ?? undefined,
  });
  const run = async (name: string, fn: () => Promise<any>, ok?: string): Promise<boolean> => {
    setBusy(name);
    setNotice(null);
    try {
      const res = await fn();
      if (res?.data?.reportId) apply(res.data);
      if (ok) setNotice({ severity: 'success', text: ok });
      return true;
    } catch (e: any) {
      setNotice({ severity: 'error', text: errorText(e, 'That did not work') });
      return false;
    } finally {
      setBusy(null);
    }
  };
  const save = async (): Promise<boolean> => {
    const problem = validate();
    if (problem) { setNotice({ severity: 'warning', text: problem }); return false; }
    return run('save', () => apiService.saveProjectReport(reportId, payload()), 'Saved.');
  };
  const refresh = async () => {
    if (dirty && !(await save())) return;
    await run('refresh', () => apiService.refreshProjectReport(reportId), 'SAP figures read again. The figures you changed on purpose were kept.');
  };
  const submit = async () => {
    setDialog(null);
    const problem = validate();
    if (problem) { setNotice({ severity: 'warning', text: problem }); return; }
    await run('submit', () => apiService.submitProjectReport(reportId, payload()), 'Submitted. Management can now read the report and its issues.');
  };
  const returnToPm = async () => {
    setDialog(null);
    await run('return', () => apiService.returnProjectReport(reportId, returnNote), 'Returned to the project manager.');
    setReturnNote('');
  };
  const remove = async () => {
    setDialog(null);
    if (await run('delete', () => apiService.deleteProjectReport(reportId))) navigate(`/project-reports?month=${view.month.slice(0, 7)}`);
  };
  const openPdf = async () => {
    if (dirty) setNotice({ severity: 'info', text: 'The PDF shows the last saved version. Save to include your changes.' });
    const win = window.open('', '_blank');
    setBusy('pdf');
    try {
      const blob = await apiService.downloadProjectReportPdf(reportId);
      const url = URL.createObjectURL(blob);
      if (win) win.location.href = url;
      else window.location.href = url;
    } catch (e: any) {
      win?.close();
      setNotice({ severity: 'error', text: errorText(e, 'The PDF could not be built') });
    } finally {
      setBusy(null);
    }
  };

  const d = data;
  const amountProps = { template: t, disabled: !editable };
  const facts = view.facts;
  const mainOrder = facts?.contract?.mainOrder;
  const otherOrders = facts?.contract?.otherOrders || [];
  const expectedPending = pendingNcrs(view.previous?.pendingNcrs ?? null, d.quality.issued, d.quality.closed);

  const cover = (
    <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 } }} component="section" aria-label="Cover">
      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Cover page</Typography>
      <Grid container spacing={1.5}>
        {t === 'KSA' && (
          <Grid item xs={12} md={4}><TextInput label="Sector" value={d.cover.sector} onChange={(v) => set('cover.sector', v)} disabled={!editable} maxLength={120} {...sap('cover.sector')} /></Grid>
        )}
        <Grid item xs={12} md={t === 'KSA' ? 4 : 6}>
          <TextInput label={t === 'KSA' ? 'Client / Customer / Owner' : 'Client'} value={d.cover.client} onChange={(v) => set('cover.client', v)} disabled={!editable} maxLength={255} {...sap('cover.client')} />
        </Grid>
        <Grid item xs={12} md={t === 'KSA' ? 4 : 6}>
          {t === 'KSA'
            ? <TextInput label="Main Contractor" value={d.cover.mainContractor} onChange={(v) => set('cover.mainContractor', v)} disabled={!editable} maxLength={255} />
            : <TextInput label="Consultant" value={d.cover.consultant} onChange={(v) => set('cover.consultant', v)} disabled={!editable} maxLength={255} />}
        </Grid>
      </Grid>
    </Paper>
  );

  const contractInfo = (no: number) => (
    <Section no={no} title="Contract Information">
      <Grid container spacing={1.5}>
        <Grid item xs={12} md={4}><TextInput label={t === 'KSA' ? 'Contract / PO Reference' : 'Contract Reference'} value={d.contract.reference} onChange={(v) => set('contract.reference', v)} disabled={!editable} maxLength={255} {...sap('contract.reference')} /></Grid>
        <Grid item xs={12} md={8}><TextInput label="Contract Title" value={d.contract.title} onChange={(v) => set('contract.title', v)} disabled={!editable} {...sap('contract.title')} /></Grid>
        <Grid item xs={12}><TextInput label="Scope of Work" value={d.contract.scope} onChange={(v) => set('contract.scope', v)} disabled={!editable} multiline minRows={2} maxLength={4000} {...sap('contract.scope')} /></Grid>
      </Grid>
    </Section>
  );

  const contractDates = (no: number) => (
    <Section no={no} title="Contract Dates">
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={6} md={t === 'KSA' ? 3 : 4}><DateInput label={t === 'KSA' ? 'Start Date' : 'Contract Start Date'} value={d.dates.startDate} onChange={(v) => set('dates.startDate', v)} disabled={!editable} {...sap('dates.startDate')} /></Grid>
        {t === 'KWT' && <Grid item xs={12} sm={6} md={4}><DateInput label="Actual Start Date" value={d.dates.actualStartDate} onChange={(v) => set('dates.actualStartDate', v)} disabled={!editable} /></Grid>}
        <Grid item xs={12} sm={6} md={t === 'KSA' ? 3 : 4}><ComputedValue label="Contract Duration (days)" value={durationDays(d.dates.startDate, d.dates.originalCompletion)?.toLocaleString('en-US') || ''} /></Grid>
        <Grid item xs={12} sm={6} md={t === 'KSA' ? 3 : 4}><DateInput label="Original Completion Date" value={d.dates.originalCompletion} onChange={(v) => set('dates.originalCompletion', v)} disabled={!editable} {...sap('dates.originalCompletion')} /></Grid>
        {t === 'KSA'
          ? <Grid item xs={12} sm={6} md={3}><DateInput label="Latest Renewal Date" value={d.dates.latestRenewal} onChange={(v) => set('dates.latestRenewal', v)} disabled={!editable} {...sap('dates.latestRenewal')} /></Grid>
          : <Grid item xs={12} sm={6} md={4}><DateInput label="Forecast Completion Date" value={d.dates.forecastCompletion} onChange={(v) => set('dates.forecastCompletion', v)} disabled={!editable} /></Grid>}
      </Grid>
    </Section>
  );

  const ordersNote = (mainOrder || otherOrders.length > 0) && (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {mainOrder ? `Main contract in SAP: sales order ${mainOrder.docNum} (${mainOrder.numAtCard || 'no reference'}), ${formatAmount(mainOrder.total, t)}${mainOrder.contractStart ? `, ${dateLabel(mainOrder.contractStart)} – ${dateLabel(mainOrder.contractEnd)}` : ''}.` : ''}
      </Typography>
      {t === 'KWT' && otherOrders.length > 0 && (
        <Typography variant="caption" color="text.secondary" display="block">
          Other sales orders on this project (variation orders?): {otherOrders.map((o) => `SO ${o.docNum} ${formatAmount(o.total, t)}${o.numAtCard ? ` (${o.numAtCard})` : ''}`).join('; ')}.
        </Typography>
      )}
    </Box>
  );

  const contractAmount = (no: number) => (
    <Section no={no} title={`Contract Amount (${t === 'KSA' ? 'SAR' : 'KWD'})`}>
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: t === 'KSA' ? 'repeat(5, 1fr)' : 'repeat(3, 1fr)' } }}>
        {t === 'KSA' ? (
          <>
            {KSA_CATEGORIES.map((c) => (
              <NumberInput key={c} label={`${KSA_CATEGORY_LABELS[c]} Amount`} value={d.amounts[c]} onChange={(v) => set(`amounts.${c}`, v)} {...amountProps} {...sap(`amounts.${c}`)} />
            ))}
            <ComputedValue label="Total" value={formatAmount(contractTotal(t, d.amounts), t)} />
          </>
        ) : (
          <>
            <NumberInput label="Main Contract Amount" value={d.amounts.main} onChange={(v) => set('amounts.main', v)} {...amountProps} {...sap('amounts.main')} />
            <NumberInput label="Approved Variation Orders" value={d.amounts.variationOrders} onChange={(v) => set('amounts.variationOrders', v)} {...amountProps} />
            <ComputedValue label="Total Contract Amount" value={formatAmount(contractTotal(t, d.amounts), t)} />
          </>
        )}
      </Box>
      {ordersNote}
    </Section>
  );

  const count = (path: string, label: string) => (
    <NumberInput label={label} value={(path.split('.').reduce<any>((o, k) => o?.[k], d) as number | null) ?? null} onChange={(v) => set(path, v)} integer disabled={!editable} />
  );

  const manpower = (no: number) => (
    <Section no={no} title="Manpower Status">
      {t === 'KSA' ? (
        <Grid container spacing={1.5}>
          <Grid item xs={6} md={3}>{count('manpower.plannedManual', 'Planned Manual')}</Grid>
          <Grid item xs={6} md={3}>{count('manpower.actualManual', 'Actual Manual')}</Grid>
          <Grid item xs={6} md={3}>{count('manpower.plannedNonManual', 'Planned Non-Manual')}</Grid>
          <Grid item xs={6} md={3}>{count('manpower.actualNonManual', 'Actual Non-Manual')}</Grid>
        </Grid>
      ) : (
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={4}>{count('manpower.plannedNonManual', 'Planned Non-Manual')}</Grid>
          <Grid item xs={12} md={4}>{count('manpower.plannedKuwaiti', 'Planned Kuwaiti')}</Grid>
          <Grid item xs={12} md={4}>{count('manpower.plannedManual', 'Planned Manual')}</Grid>
          <Grid item xs={12} md={4}>{count('manpower.actualNonManual', 'Actual Non-Manual')}</Grid>
          <Grid item xs={12} md={4}>{count('manpower.actualKuwaiti', 'Actual Kuwaiti')}</Grid>
          <Grid item xs={12} md={4}>{count('manpower.actualManual', 'Actual Manual')}</Grid>
        </Grid>
      )}
    </Section>
  );

  const activityList = (kind: 'planned' | 'actual', label: string) => (
    <Box sx={{ display: 'grid', gap: 1 }}>
      <Typography variant="subtitle2">{label}</Typography>
      {d.activities[kind].length === 0 && <Typography variant="body2" color="text.secondary">None yet.</Typography>}
      {d.activities[kind].map((a, i) => (
        <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
          <TextField size="small" fullWidth multiline value={a} disabled={!editable} inputProps={{ 'aria-label': `${label} ${i + 1}`, maxLength: 500 }}
            onChange={(e) => set(`activities.${kind}`, d.activities[kind].map((x, j) => (j === i ? e.target.value : x)))} />
          {editable && <IconButton aria-label="Remove activity" size="small" onClick={() => set(`activities.${kind}`, d.activities[kind].filter((_x, j) => j !== i))}><DeleteOutlineIcon fontSize="small" /></IconButton>}
        </Stack>
      ))}
      {editable && <Box><Button size="small" startIcon={<AddIcon />} onClick={() => set(`activities.${kind}`, [...d.activities[kind], ''])}>Add</Button></Box>}
    </Box>
  );

  const safety = (no: number) => (
    <Section no={no} title="Safety Issues">
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        <Grid item xs={12} sm={6} md={3}>{count('safety.lostTimeIncidents', 'Lost Time Incidents')}</Grid>
      </Grid>
      <IssueList section="SAFETY" title="Items of concern" issues={issues} onChange={setIssueList} month={view.month} disabled={!editable} invalid={invalid} />
    </Section>
  );

  const quality = (no: number) => (
    <Section no={no} title="Quality Issues">
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={4}>{count('quality.issued', "Issued NCR's (Number)")}</Grid>
        <Grid item xs={12} sm={4}>{count('quality.closed', t === 'KSA' ? "Resolved NCR's (Number)" : "Closed NCR's (Number)")}</Grid>
        {t === 'KWT' && (
          <Grid item xs={12} sm={4}>
            <NumberInput
              label="Pending NCR's (Number)"
              value={d.quality.pending}
              onChange={(v) => set('quality.pending', v)}
              integer
              disabled={!editable}
              helperText={expectedPending !== null && expectedPending !== d.quality.pending ? (
                <Typography component="span" variant="caption" color="text.secondary">
                  Previous pending {view.previous?.pendingNcrs ?? 0} + issued − closed = {expectedPending}
                  {editable && <> · <Link component="button" type="button" variant="caption" onClick={() => set('quality.pending', expectedPending)}>use</Link></>}
                </Typography>
              ) : undefined}
            />
          </Grid>
        )}
      </Grid>
    </Section>
  );

  const concerns = (no: number) => (
    <Section no={no} title="Area of Concerns">
      <Box sx={{ display: 'grid', gap: 2.5 }}>
        <IssueList section="INTERNAL" title="Internal issues" issues={issues} onChange={setIssueList} month={view.month} disabled={!editable} invalid={invalid} />
        <IssueList section="EXTERNAL" title="External issues" issues={issues} onChange={setIssueList} month={view.month} disabled={!editable} invalid={invalid} />
      </Box>
    </Section>
  );

  const sections = t === 'KSA' ? (
    <>
      {contractInfo(1)}
      {contractDates(2)}
      {contractAmount(3)}
      <Section no={4} title="Progress">
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={4}><DateInput label="Actual Start Date" value={d.dates.actualStartDate} onChange={(v) => set('dates.actualStartDate', v)} disabled={!editable} /></Grid>
          <Grid item xs={12} sm={4}><DateInput label="Forecast Completion Date" value={d.dates.forecastCompletion} onChange={(v) => set('dates.forecastCompletion', v)} disabled={!editable} /></Grid>
          <Grid item xs={12} sm={4}><NumberInput label="% of Mobilization" value={d.progress.mobilizationPct} onChange={(v) => set('progress.mobilizationPct', v === null ? null : Math.min(Math.max(v, 0), 100))} suffix="%" disabled={!editable} /></Grid>
        </Grid>
      </Section>
      {manpower(5)}
      {safety(6)}
      {quality(7)}
      {concerns(8)}
    </>
  ) : (
    <>
      {contractInfo(1)}
      {contractDates(2)}
      {contractAmount(3)}
      {manpower(4)}
      <Section no={5} title="Subcontractors Progress, if any">
        <SubcontractorsTable rows={d.subcontractors} template={t} disabled={!editable} onChange={(rows) => set('subcontractors', rows)} />
      </Section>
      <Section no={6} title="Schedule of Ongoing Activities & Progress of Work">
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>{activityList('planned', 'Planned')}</Grid>
          <Grid item xs={12} md={6}>{activityList('actual', 'Actual')}</Grid>
        </Grid>
      </Section>
      {safety(7)}
      {quality(8)}
      {concerns(9)}
    </>
  );

  const busyIcon = (name: string, icon: React.ReactNode) => (busy === name ? <CircularProgress size={16} color="inherit" /> : icon);

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, display: 'grid', gap: 2, maxWidth: 1400 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ md: 'flex-start' }}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Tooltip title="Monthly Reports">
            <IconButton aria-label="Back to the monthly reports" onClick={() => navigate(`/project-reports?month=${view.month.slice(0, 7)}`)}><ArrowBackIcon /></IconButton>
          </Tooltip>
          <Box>
            <Typography variant="h5" component="h1" sx={{ wordBreak: 'break-word' }}>
              {view.projectCode} · {view.projectName}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
              <Chip size="small" label={view.status} color={view.status === 'Submitted' ? 'success' : 'warning'} />
              <Typography variant="body2" color="text.secondary">
                Monthly Progress Report · {view.monthLabel} · {TEMPLATE_LABELS[t]} · {view.companyName}
              </Typography>
            </Stack>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent={{ md: 'flex-end' }}>
          {view.permissions.canEdit && (
            <>
              <Button variant="contained" startIcon={busyIcon('save', <SaveIcon />)} disabled={!!busy || !dirty} onClick={() => void save()}>Save</Button>
              <Button variant="outlined" startIcon={busyIcon('refresh', <SyncIcon />)} disabled={!!busy} onClick={() => void refresh()}>Read SAP again</Button>
            </>
          )}
          <Button variant="outlined" startIcon={busyIcon('pdf', <PictureAsPdfIcon />)} disabled={!!busy} onClick={() => void openPdf()}>PDF</Button>
          {view.permissions.canSubmit && (
            <Button variant="contained" color="success" startIcon={busyIcon('submit', <SendIcon />)} disabled={!!busy} onClick={() => setDialog('submit')}>Submit</Button>
          )}
          {view.permissions.canReturn && (
            <Button variant="outlined" color="warning" startIcon={busyIcon('return', <UndoIcon />)} disabled={!!busy} onClick={() => setDialog('return')}>Return to PM</Button>
          )}
          {view.permissions.canDelete && (
            <Button color="error" startIcon={busyIcon('delete', <DeleteOutlineIcon />)} disabled={!!busy} onClick={() => setDialog('delete')}>Delete draft</Button>
          )}
        </Stack>
      </Stack>

      {notice && <Alert severity={notice.severity} onClose={() => setNotice(null)}>{notice.text}</Alert>}
      {view.status === 'Draft' && view.returnNote && view.returnedAt && (
        <Alert severity="warning">
          Returned by {view.returnedBy || 'management'} on {new Date(view.returnedAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}: {view.returnNote}
        </Alert>
      )}
      {(facts?.warnings || []).length > 0 && (
        <Alert severity="warning">
          Some SAP figures could not be read: {facts?.warnings.join('; ')}. Enter them yourself or read SAP again.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 } }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={6} sm={3} md={2}>
            <NumberInput label="Report No." value={reportNo} onChange={(v) => { setReportNo(v); setDirty(true); }} integer disabled={!editable} helperText={view.previous ? undefined : 'First report: set the number you continue from'} />
          </Grid>
          <Grid item xs={6} sm={3} md={2}><ComputedValue label="Month" value={view.monthLabel} /></Grid>
          <Grid item xs={12} sm={6} md={4}><ComputedValue label="Prepared by" value={view.preparedBy.name} note={view.submittedAt ? `Submitted ${new Date(view.submittedAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}${view.submittedBy ? ` by ${view.submittedBy}` : ''}` : undefined} /></Grid>
          <Grid item xs={12} md={4}>
            <ComputedValue label="SAP figures read" value={view.sapReadAt ? new Date(view.sapReadAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : 'Never'} note={facts?.project?.members && facts.project.members.length > 1 ? `${facts.project.members.length} SAP projects (with sub-projects)` : undefined} />
          </Grid>
        </Grid>
      </Paper>

      {cover}
      {sections}

      {view.events.length > 0 && (
        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 } }} component="section" aria-label="History">
          <Typography variant="subtitle2" sx={{ mb: 1 }}>History</Typography>
          {view.events.map((e, i) => (
            <Typography key={i} variant="body2" color="text.secondary">
              {new Date(e.at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })} · {e.action}{e.user ? ` by ${e.user}` : ''}{e.note ? ` · ${e.note}` : ''}
            </Typography>
          ))}
        </Paper>
      )}

      <Dialog open={dialog === 'submit'} onClose={() => setDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Submit the {view.monthLabel} report?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Management will read it and its {issues.filter((i) => i.description.trim()).length} ranked issue(s). You can no longer change it unless management returns it to you.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={() => void submit()}>Submit</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialog === 'return'} onClose={() => setDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Return to the project manager</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth multiline minRows={3} label="What should be corrected?" value={returnNote} onChange={(e) => setReturnNote(e.target.value)} inputProps={{ maxLength: 1000 }} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button variant="contained" color="warning" disabled={!returnNote.trim()} onClick={() => void returnToPm()}>Return</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialog === 'delete'} onClose={() => setDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete this draft?</DialogTitle>
        <DialogContent><DialogContentText>The draft and its issues are deleted. You can prepare the month again afterwards.</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => void remove()}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectReportEditor;
